import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import PeopleIcon from "@material-ui/icons/People";
import EditIcon from "@material-ui/icons/Edit";
import StarIcon from "@material-ui/icons/Star";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  metricCard: {
    padding: theme.spacing(2),
    borderRadius: 12,
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },
  metricIcon: {
    position: "absolute",
    right: 16,
    top: 16,
    opacity: 0.2,
    fontSize: 48,
  },
  metricValue: {
    fontSize: "2rem",
    fontWeight: "bold",
  },
  metricLabel: {
    fontSize: "0.8rem",
    color: "#666",
    textTransform: "uppercase",
    marginTop: 4,
  },
  progressSection: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  progressCard: {
    padding: theme.spacing(2),
    borderRadius: 12,
  },
  progressBar: {
    height: 12,
    borderRadius: 6,
    marginTop: theme.spacing(1),
  },
  rankingTable: {
    borderRadius: 12,
    overflow: "hidden",
  },
  rankingHeader: {
    backgroundColor: "#1a5c1a",
    "& th": {
      color: "#fff",
      fontWeight: 600,
    },
  },
  goldRow: {
    backgroundColor: "#fff8e1",
  },
  silverRow: {
    backgroundColor: "#f5f5f5",
  },
  bronzeRow: {
    backgroundColor: "#fff3e0",
  },
  medalChip: {
    fontWeight: "bold",
  },
}));

const SalesGoals = () => {
  const classes = useStyles();
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [openGoalModal, setOpenGoalModal] = useState(false);
  const [teamData, setTeamData] = useState([]);
  const [goals, setGoals] = useState({
    metaMensal: 500000,
    metaContratos: 15,
    comissaoPercentual: 3.5,
  });

  useEffect(() => {
    loadData();
  }, [month, year]);

  const loadData = async () => {
    try {
      const { data } = await api.get(`/crm/goals?month=${month + 1}&year=${year}`);
      setTeamData(data.team);
      setGoals(data.goals);
    } catch (err) {
      // Sem dados cadastrados
      setTeamData([]);
      setGoals({ metaMensal: 0, metaContratos: 0, comissaoPercentual: 0 });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const totalVendas = teamData.reduce((sum, t) => sum + t.valorTotal, 0);
  const totalContratos = teamData.reduce((sum, t) => sum + t.contratos, 0);
  const totalComissao = teamData.reduce((sum, t) => sum + t.comissao, 0);
  const progressoMeta = (totalVendas / goals.metaMensal) * 100;

  const getMedalColor = (index) => {
    if (index === 0) return "#ffd700";
    if (index === 1) return "#c0c0c0";
    if (index === 2) return "#cd7f32";
    return "#9e9e9e";
  };

  const getRowClass = (index) => {
    if (index === 0) return classes.goldRow;
    if (index === 1) return classes.silverRow;
    if (index === 2) return classes.bronzeRow;
    return "";
  };

  const sortedTeam = [...teamData].sort((a, b) => b.valorTotal - a.valorTotal);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Metas e Comissões
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Acompanhamento de desempenho da equipe de vendas
          </Typography>
        </div>
        <Box display="flex" style={{ gap: 8 }}>
          <TextField
            select
            size="small"
            variant="outlined"
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
            style={{ width: 130 }}
          >
            {["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"].map((m, i) => (
              <MenuItem key={i} value={i}>{m}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setOpenGoalModal(true)}
          >
            Definir Metas
          </Button>
        </Box>
      </div>

      {/* Cards de Métricas */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.metricCard} style={{ borderLeft: "4px solid #2e7d32" }}>
            <AttachMoneyIcon className={classes.metricIcon} style={{ color: "#2e7d32" }} />
            <Typography className={classes.metricValue} style={{ color: "#2e7d32" }}>
              {formatCurrency(totalVendas)}
            </Typography>
            <Typography className={classes.metricLabel}>Total Vendido</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.metricCard} style={{ borderLeft: "4px solid #1976d2" }}>
            <TrendingUpIcon className={classes.metricIcon} style={{ color: "#1976d2" }} />
            <Typography className={classes.metricValue} style={{ color: "#1976d2" }}>
              {totalContratos}
            </Typography>
            <Typography className={classes.metricLabel}>Contratos Fechados</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.metricCard} style={{ borderLeft: "4px solid #ff9800" }}>
            <EmojiEventsIcon className={classes.metricIcon} style={{ color: "#ff9800" }} />
            <Typography className={classes.metricValue} style={{ color: "#ff9800" }}>
              {formatCurrency(totalComissao)}
            </Typography>
            <Typography className={classes.metricLabel}>Comissões Geradas</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.metricCard} style={{ borderLeft: "4px solid #9c27b0" }}>
            <PeopleIcon className={classes.metricIcon} style={{ color: "#9c27b0" }} />
            <Typography className={classes.metricValue} style={{ color: "#9c27b0" }}>
              {teamData.length}
            </Typography>
            <Typography className={classes.metricLabel}>Vendedores Ativos</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Progresso da Meta */}
      <div className={classes.progressSection}>
        <Paper className={classes.progressCard}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Meta Mensal da Equipe</Typography>
            <Typography variant="h6" style={{ color: progressoMeta >= 100 ? "#2e7d32" : "#ff9800" }}>
              {progressoMeta.toFixed(1)}%
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
            <Typography variant="body2" color="textSecondary">
              {formatCurrency(totalVendas)} de {formatCurrency(goals.metaMensal)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Faltam {formatCurrency(Math.max(0, goals.metaMensal - totalVendas))}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(progressoMeta, 100)}
            className={classes.progressBar}
            style={{
              backgroundColor: "#e0e0e0",
            }}
          />
        </Paper>
      </div>

      {/* Ranking da Equipe */}
      <TableContainer component={Paper} className={classes.rankingTable}>
        <Table>
          <TableHead className={classes.rankingHeader}>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Vendedor</TableCell>
              <TableCell align="center">Contratos</TableCell>
              <TableCell align="right">Valor Vendido</TableCell>
              <TableCell align="center">Meta Individual</TableCell>
              <TableCell align="center">Progresso</TableCell>
              <TableCell align="right">Comissão</TableCell>
              <TableCell align="center">Conversão</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTeam.map((member, index) => {
              const progress = (member.valorTotal / member.meta) * 100;
              return (
                <TableRow key={member.id} className={getRowClass(index)}>
                  <TableCell>
                    <Chip
                      icon={index < 3 ? <StarIcon style={{ color: getMedalColor(index) }} /> : undefined}
                      label={index + 1}
                      size="small"
                      className={classes.medalChip}
                      style={{
                        backgroundColor: index < 3 ? `${getMedalColor(index)}22` : "#f5f5f5",
                        border: `1px solid ${getMedalColor(index)}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Avatar style={{ width: 32, height: 32, backgroundColor: getMedalColor(index) }}>
                        {member.name.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {member.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={member.contratos} size="small" color="primary" />
                  </TableCell>
                  <TableCell align="right" style={{ fontWeight: 600, color: "#2e7d32" }}>
                    {formatCurrency(member.valorTotal)}
                  </TableCell>
                  <TableCell align="center">
                    {formatCurrency(member.meta)}
                  </TableCell>
                  <TableCell align="center" style={{ width: 150 }}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(progress, 100)}
                        style={{ flex: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" style={{ fontWeight: 600 }}>
                        {progress.toFixed(0)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right" style={{ fontWeight: 600, color: "#ff9800" }}>
                    {formatCurrency(member.comissao)}
                  </TableCell>
                  <TableCell align="center">
                    {member.conversao}%
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Definir Metas */}
      <Dialog open={openGoalModal} onClose={() => setOpenGoalModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Definir Metas do Mês</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ paddingTop: 8 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meta Mensal da Equipe (R$)"
                value={goals.metaMensal}
                onChange={(e) => setGoals({ ...goals, metaMensal: parseFloat(e.target.value) || 0 })}
                variant="outlined"
                size="small"
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Meta de Contratos"
                value={goals.metaContratos}
                onChange={(e) => setGoals({ ...goals, metaContratos: parseInt(e.target.value) || 0 })}
                variant="outlined"
                size="small"
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Comissão (%)"
                value={goals.comissaoPercentual}
                onChange={(e) => setGoals({ ...goals, comissaoPercentual: parseFloat(e.target.value) || 0 })}
                variant="outlined"
                size="small"
                type="number"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGoalModal(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => { setOpenGoalModal(false); toast.success("Metas atualizadas!"); }}
            style={{ backgroundColor: "#2e7d32", color: "#fff" }}
          >
            Salvar Metas
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SalesGoals;
